import type { Data } from '@dnd-kit/abstract'
import { SortableDraggable } from '@dnd-kit/dom/sortable'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex } from '@/components/ui/flex'

import { BlockCard } from '../block-card'
import { SortableBlockItem, StaticBlockItem } from '../block-item'
import {
  HomepageConfigResponse,
  HomepageWidgetType,
  HomepageWidgetTypeEnum,
} from '../home-screen.types'
import { isWidgetEnabled } from '../home-screen.utils'

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>

interface WidgetsBlockProps {
  config: HomepageConfigResponse
  order: HomepageWidgetType[]
  widgetLabels: Record<HomepageWidgetType, string>
  disabled: boolean
  onToggle: (widget: HomepageWidgetType) => void
  onReorder: (sourceIndex: number, targetIndex: number) => void
}

// buildingImage, when enabled, stays pinned to the top and locked - the backend requires it at
// position 0 and never draggable.
export const WidgetsBlock = ({
  config,
  order,
  widgetLabels,
  disabled,
  onToggle,
  onReorder,
}: WidgetsBlockProps) => {
  const { t } = useTranslation(['home-screen'])

  const isPinned = config.buildingImageEnabled
  const sortable = isPinned
    ? order.filter((widget) => widget !== HomepageWidgetTypeEnum.enum.buildingImage)
    : order

  const handleDragEnd: DragEndHandler = (event) => {
    const { canceled, operation } = event
    if (canceled) return

    const { source } = operation
    if (!source || !isSortable(source)) return

    const sortableSource = source as SortableDraggable<Data>
    const sourceIndex = sortableSource.initialIndex
    const targetIndex = sortableSource.index
    if (sourceIndex === targetIndex) return

    const offset = isPinned ? 1 : 0
    onReorder(sourceIndex + offset, targetIndex + offset)
  }

  return (
    <BlockCard
      title={t('home-screen:labels.widgetsConfigurationHeader')}
      tooltip={t('home-screen:tooltips.widgetsConfiguration')}
    >
      <Flex direction="column" gap={8}>
        {isPinned && (
          <StaticBlockItem
            checked
            disabled={disabled}
            label={widgetLabels.buildingImage}
            onToggle={() => onToggle(HomepageWidgetTypeEnum.enum.buildingImage)}
          />
        )}
        <DragDropProvider onDragEnd={handleDragEnd}>
          {sortable.map((widget, index) => (
            <SortableBlockItem
              key={widget}
              checked={isWidgetEnabled(config, widget)}
              disabled={disabled}
              id={`widget-${widget}`}
              index={index}
              label={widgetLabels[widget]}
              onToggle={() => onToggle(widget)}
            />
          ))}
        </DragDropProvider>
      </Flex>
    </BlockCard>
  )
}
